// Host-aware routing for the dmhw bundle.
//
// One deployed bundle serves two faces, chosen by hostname:
//   • app.rainmkr.co.za    → the new-build modules app (default)
//   • legacy.rainmkr.co.za → the PMO Director Command Centre (/cc/director), Ravi-only
//
// There is no existing isHempireHost() to mirror (the Hempire build lives in a
// separate repo/deploy), so this is the single source of truth for host checks
// in this app. Keep host logic here so router + shell agree.

// Hosts that should serve the legacy PMO face. Lane A will supply the real
// www/apex aliases (e.g. the naked domain, a www. variant) at apply-prep —
// add them to this array; every consumer picks them up automatically.
const LEGACY_HOSTS: string[] = [
  'legacy.rainmkr.co.za',
  // Lane A: add legacy-host aliases here (www/apex) before go-live.
];

/** True when the current page is being served from a legacy PMO host. */
export function isLegacyHost(): boolean {
  if (typeof window === 'undefined') return false;
  return LEGACY_HOSTS.includes(window.location.hostname);
}

// The single production hostname for the new-build modules app. The "View As"
// role-preview tool (session.viewAsRole + ViewAsSwitcher/banner) is a
// staging-only developer aid: it must NEVER render or take effect on prod, so it
// is gated on `!isProdHost()`. Keeping the check here (not inlined at each call
// site) means Netlify-prod suppression is enforced in one place — Ravi's
// "remove before go-live" happens automatically by hostname.
const PROD_HOST = 'app.rainmkr.co.za';

/** True only on the production modules host — used to suppress staging-only tooling. */
export function isProdHost(): boolean {
  if (typeof window === 'undefined') return false;
  return window.location.hostname === PROD_HOST;
}

/**
 * True when write controls should be disabled for the current page.
 *
 * The legacy PMO Command Centre is served on the legacy host and is a retiring,
 * frozen reference: BOTH founders can VIEW it, but nobody writes to it. Write
 * controls are disabled there for all users. This is a UX-level guard so the
 * frozen tool still shows what it did — it is NOT a security control; RLS at the
 * database remains the real boundary.
 */
export function isLegacyReadOnly(): boolean {
  return isLegacyHost();
}

// Cross-host switch targets. These are ABSOLUTE production hosts on purpose:
// the two faces live on two different hostnames, so switching between them is a
// hard cross-host navigation, not a client-side route. Because they are absolute
// prod URLs they are NOT exercised on the Vercel *.vercel.app staging deploy —
// they are verified by code review + Lane A's live smoke test.
// Do NOT make these host-relative: '/home' served from the legacy host renders
// the SaaS home with the module nav hidden (wrong face).
export const SAAS_HOME_URL = 'https://app.rainmkr.co.za/home';
export const LEGACY_HOME_URL = 'https://legacy.rainmkr.co.za/';
